/**
 * Renders a repeat band: the rows a printed form gives a list of records.
 *
 * The band's `capacity` is how many rows the printed page holds. Going over it
 * is allowed, because the Word exporter clones a continuation page, but the
 * card says so rather than letting the overflow come as a surprise at export
 * time.
 *
 * Editing is per row, not per band, following the ICS-204's resource and
 * communications tables: one row is editable at a time, confirming a row writes
 * the whole record, and a row added but never confirmed is dropped rather than
 * saved blank.
 */

import type React from 'react';
import { useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Check, Pencil, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { FormSection, FormSectionColumn } from './formSectionSchema';
import { useDescriptorSection } from './descriptorSectionApi';

type RowValues = Record<string, string | undefined>;

const blankRow = (columns: FormSectionColumn[]): RowValues =>
  columns.reduce<RowValues>((row, column) => {
    row[column.field] = '';
    return row;
  }, {});

const DescriptorTableRow: React.FC<{
  source: string;
  index: number;
  columns: FormSectionColumn[];
  editing: boolean;
  disabled: boolean;
  onEdit: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onRemove: () => void;
}> = ({
  source,
  index,
  columns,
  editing,
  disabled,
  onEdit,
  onConfirm,
  onCancel,
  onRemove,
}) => {
  const { control } = useFormContext();
  // Watched rather than read once, so a confirmed or reverted row shows the
  // value it settled on without the band having to re-key the row.
  const row = useWatch({ control, name: `${source}.${index}` }) as
    | RowValues
    | undefined;

  const label = `row ${index + 1}`;

  return (
    <TableRow>
      {columns.map((column) =>
        editing ? (
          <TableCell key={column.field}>
            <Controller
              control={control}
              name={`${source}.${index}.${column.field}`}
              render={({ field }) => (
                <Input
                  aria-label={`${column.label} ${label}`}
                  disabled={disabled}
                  name={field.name}
                  value={(field.value as string | undefined) ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </TableCell>
        ) : (
          <TableCell key={column.field}>
            {row?.[column.field]?.trim() ? (
              row[column.field]
            ) : (
              <span className="tw:text-muted-foreground">—</span>
            )}
          </TableCell>
        )
      )}
      <TableCell>
        <div className="tw:flex tw:gap-1">
          {editing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={`Save ${label}`}
                onClick={onConfirm}
              >
                <Check className="tw:h-4 tw:w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={`Cancel ${label}`}
                onClick={onCancel}
              >
                <X className="tw:h-4 tw:w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={`Edit ${label}`}
                onClick={onEdit}
              >
                <Pencil className="tw:h-4 tw:w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={`Remove ${label}`}
                onClick={onRemove}
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export type DescriptorSectionTableProps = {
  section: FormSection;
  heading: string;
  helper: string | null;
  disabled?: boolean;
};

export const DescriptorSectionTable: React.FC<DescriptorSectionTableProps> = ({
  section,
  heading,
  helper,
  disabled = false,
}) => {
  const { control, getValues, resetField } = useFormContext();
  const { values, commit } = useDescriptorSection();
  const { fields, append, remove } = useFieldArray({
    control,
    name: section.source,
  });

  /** The row open for editing, and whether it has ever been written. */
  const [editing, setEditing] = useState<{
    index: number;
    isNew: boolean;
  } | null>(null);

  const columns = section.columns ?? [];
  const overflowing =
    section.capacity !== undefined && fields.length > section.capacity;

  const addRow = () => {
    append(blankRow(columns));
    setEditing({ index: fields.length, isNew: true });
  };

  const confirmRow = () => {
    commit([section.source]);
    setEditing(null);
  };

  const cancelRow = () => {
    if (!editing) return;
    if (editing.isNew) {
      remove(editing.index);
    } else {
      const original = (values[section.source] as RowValues[] | undefined)?.[
        editing.index
      ];
      resetField(`${section.source}.${editing.index}`, {
        defaultValue: original,
      });
    }
    setEditing(null);
  };

  /**
   * Deleting persists straight away, as it does on the 204: there is no row
   * left to hold a pending change on. The shortened list is passed explicitly
   * so the write does not depend on when the field array flushes.
   */
  const removeRow = (index: number) => {
    const rows = (getValues(section.source) as RowValues[] | undefined) ?? [];
    const next = rows.filter((_, position) => position !== index);
    remove(index);
    setEditing(null);
    commit([section.source], { [section.source]: next });
  };

  return (
    <Field className="tw:gap-2" data-section={section.source}>
      <FieldLabel>{heading}</FieldLabel>
      {helper ? <FieldDescription>{helper}</FieldDescription> : null}

      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.field}>{column.label}</TableHead>
            ))}
            <TableHead className="tw:w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                className="tw:text-muted-foreground"
              >
                No rows yet.
              </TableCell>
            </TableRow>
          ) : (
            fields.map((row, index) => (
              <DescriptorTableRow
                key={row.id}
                source={section.source}
                index={index}
                columns={columns}
                editing={editing?.index === index}
                disabled={disabled}
                onEdit={() => setEditing({ index, isNew: false })}
                onConfirm={confirmRow}
                onCancel={cancelRow}
                onRemove={() => removeRow(index)}
              />
            ))
          )}
        </TableBody>
      </Table>

      <div className="tw:flex tw:items-center tw:gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || editing !== null}
          onClick={addRow}
        >
          Add row
        </Button>
        {overflowing ? (
          <FieldDescription className="tw:m-0">
            The printed form has {section.capacity} rows; the rest continue on
            an added page.
          </FieldDescription>
        ) : null}
      </div>
    </Field>
  );
};
